import React, { useState, useMemo } from 'react';

export default function TransactionsTable({ orders, refundedIds = new Set(), refundQueue = {} }) {
  const [subTab, setSubTab]         = useState('paid');       // 'paid' | 'expired'
  const [dateFilter, setDateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showRefunded, setShowRefunded] = useState(false);

  // ── Expired entries: payment link created but customer never paid ───────────
  const expiredEntries = useMemo(() => {
    return Object.entries(refundQueue)
      .filter(([, v]) => v?.status === 'EXPIRED')
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
  }, [refundQueue]);

  // ── Paid orders (real transactions) ────────────────────────────────────────
  const filteredOrders = useMemo(() => {
    let filtered = [...orders];

    if (!showRefunded) {
      filtered = filtered.filter((o) => !refundedIds.has(o.id));
    }

    if (dateFilter !== 'all') {
      const now = Date.now();
      const ranges = { today: 86400000, week: 604800000, month: 2592000000 };
      const cutoff = now - (ranges[dateFilter] || 0);
      filtered = filtered.filter((o) => (o.timestamp || 0) * 1000 >= cutoff);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          (o.id && o.id.toLowerCase().includes(q)) ||
          (o.kiosk_id && o.kiosk_id.toLowerCase().includes(q)) ||
          (o.item_id && o.item_id.toLowerCase().includes(q))
      );
    }

    return filtered;
  }, [orders, dateFilter, searchQuery, showRefunded, refundedIds]);

  // Stuck: PAID for >5 min with no dispense
  const stuckOrders = useMemo(() => {
    const fiveMinsAgo = Date.now() - 5 * 60 * 1000;
    return orders.filter((o) => {
      if (refundedIds.has(o.id)) return false;
      if (o.status === 'DISPENSED') return false;
      if (o.status !== 'PAID') return false;
      return (o.timestamp || 0) * 1000 < fiveMinsAgo;
    });
  }, [orders, refundedIds]);

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  };

  const formatDispenseTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  };

  const getStatusBadge = (tx) => {
    const isRefundPending = refundQueue[tx.id]?.status === 'REFUND_PENDING';
    const isRefunded      = refundedIds.has(tx.id) && refundQueue[tx.id]?.status === 'REFUNDED';
    if (isRefunded)      return <span className="status-badge" style={{ background: 'rgba(255,68,68,0.15)', color: '#ff6b6b' }}>REFUNDED</span>;
    if (isRefundPending) return <span className="status-badge" style={{ background: 'rgba(255,140,0,0.2)', color: '#ffa500' }}>⏳ REFUND PENDING</span>;
    if (tx.status === 'DISPENSED') return <span className="status-badge status-badge--success">✅ DISPENSED</span>;
    if (tx.status === 'PAID') {
      const isOld = (tx.timestamp || 0) * 1000 < Date.now() - 5 * 60 * 1000;
      if (isOld) return <span className="status-badge" style={{ background: 'rgba(255,200,0,0.2)', color: '#ffd700' }} title="Paid but no dispense recorded">⚠️ PAID (no dispense)</span>;
      return <span className="status-badge status-badge--pending">💳 PAID</span>;
    }
    return <span className="status-badge status-badge--pending">{tx.status || 'Unknown'}</span>;
  };

  const refundedCount = orders.filter(o => refundedIds.has(o.id)).length;

  // ── Sub-tab pill styles ─────────────────────────────────────────────────────
  const tabStyle = (active) => ({
    padding: '0.35rem 1rem', borderRadius: '999px', border: 'none',
    cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
    background: active ? 'rgba(0,191,255,0.18)' : 'transparent',
    color: active ? '#00bfff' : '#666',
    transition: 'all 0.2s',
  });

  return (
    <div className="section">

      {/* ── Sub-tab switcher ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem',
        borderBottom: '1px solid #1a1a2e', paddingBottom: '0.75rem' }}>
        <button style={tabStyle(subTab === 'paid')} onClick={() => setSubTab('paid')}>
          🧾 Paid Orders
          <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem',
            background: 'rgba(0,255,153,0.12)', color: '#00ff99',
            padding: '1px 6px', borderRadius: '999px' }}>
            {orders.length}
          </span>
        </button>
        <button style={tabStyle(subTab === 'expired')} onClick={() => setSubTab('expired')}>
          ⏰ Expired (No Payment)
          {expiredEntries.length > 0 && (
            <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem',
              background: 'rgba(120,120,120,0.18)', color: '#888',
              padding: '1px 6px', borderRadius: '999px' }}>
              {expiredEntries.length}
            </span>
          )}
        </button>
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {subTab === 'paid' && (
        <>
          {/* Alert banner for stuck orders */}
          {stuckOrders.length > 0 && (
            <div style={{
              background: 'rgba(255,200,0,0.1)', border: '1px solid rgba(255,200,0,0.4)',
              borderRadius: '0.75rem', padding: '0.875rem 1.25rem', marginBottom: '1rem',
              display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
            }}>
              <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>⚠️</span>
              <div>
                <strong style={{ color: '#ffd700' }}>
                  {stuckOrders.length} order{stuckOrders.length > 1 ? 's' : ''} paid but not dispensed
                </strong>
                <p style={{ color: '#aaa', fontSize: '0.8rem', margin: '4px 0 0' }}>
                  {stuckOrders.map(o => o.id).join(', ')} — customer may not have received juice.
                </p>
              </div>
            </div>
          )}

          <div className="section-header">
            <h2 className="section-title">
              <span className="section-title-icon">🧾</span>
              Transaction Log
              {refundedCount > 0 && (
                <span style={{ marginLeft: '0.75rem', fontSize: '0.75rem', fontWeight: 500,
                  backgroundColor: 'rgba(255,68,68,0.15)', color: '#ff6b6b',
                  padding: '2px 8px', borderRadius: '999px' }}>
                  {refundedCount} refunded
                </span>
              )}
              {stuckOrders.length > 0 && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: 500,
                  backgroundColor: 'rgba(255,200,0,0.15)', color: '#ffd700',
                  padding: '2px 8px', borderRadius: '999px' }}>
                  {stuckOrders.length} ⚠️ stuck
                </span>
              )}
            </h2>
            <div className="section-actions">
              <input id="search-transactions" type="text" placeholder="Search orders..."
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input" />
              <button id="toggle-refunded"
                className={`date-filter-btn ${showRefunded ? 'date-filter-btn--active' : ''}`}
                onClick={() => setShowRefunded(!showRefunded)}>
                {showRefunded ? '🔴 Hide Refunded' : '🔴 Show Refunded'}
              </button>
              <div className="date-filter-group">
                {[{ key: 'all', label: 'All Time' }, { key: 'today', label: 'Today' },
                  { key: 'week', label: '7 Days' }, { key: 'month', label: '30 Days' }].map((f) => (
                  <button key={f.key} id={`filter-${f.key}`}
                    className={`date-filter-btn ${dateFilter === f.key ? 'date-filter-btn--active' : ''}`}
                    onClick={() => setDateFilter(f.key)}>{f.label}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead><tr>
                <th>Order ID</th><th>Kiosk</th><th>Beverage</th>
                <th>Amount</th><th>Payment Time</th><th>Dispense Time</th><th>Status</th>
              </tr></thead>
              <tbody>
                {filteredOrders.slice(0, 50).map((tx) => {
                  const isRefunded = refundedIds.has(tx.id);
                  const isStuck    = stuckOrders.some(o => o.id === tx.id);
                  return (
                    <tr key={tx.id} style={{ opacity: isRefunded ? 0.6 : 1,
                      background: isStuck ? 'rgba(255,200,0,0.04)' : undefined }}>
                      <td className="table-mono" style={{ fontSize: '0.75rem' }}>{tx.id}</td>
                      <td className="table-accent">{tx.kiosk_id ? tx.kiosk_id.replace('CHILL-RAS-', '') : '—'}</td>
                      <td className="table-primary">{tx.item_id ? tx.item_id.replace('juice_', '').replace(/_/g, ' ').toUpperCase() : '—'}</td>
                      <td className={isRefunded ? '' : tx.status === 'DISPENSED' ? 'table-green' : ''}
                        style={isRefunded ? { color: '#ff6b6b', textDecoration: 'line-through' } : {}}>
                        ₹{tx.amount || 19}
                      </td>
                      <td className="table-muted">{formatTimestamp(tx.timestamp)}</td>
                      <td style={{ color: tx.dispense_time ? '#00FF99' : '#444', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {tx.dispense_time ? formatDispenseTime(tx.dispense_time) : '—'}
                      </td>
                      <td>{getStatusBadge(tx)}</td>
                    </tr>
                  );
                })}
                {filteredOrders.length === 0 && (
                  <tr><td colSpan="7" className="table-empty">
                    {orders.length === 0 ? 'No transactions recorded yet.' : 'No matching transactions found.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="table-footer">
            <span className="table-count">
              Showing {Math.min(filteredOrders.length, 50)} of {filteredOrders.length} transactions
              {!showRefunded && refundedCount > 0 && ` (${refundedCount} refunded hidden)`}
            </span>
            <span style={{ fontSize: '0.7rem', color: '#555', marginLeft: '1rem' }}>
              ✅ DISPENSED = juice poured &nbsp;|&nbsp; ⚠️ = possible issue &nbsp;|&nbsp; 🔴 = refunded
            </span>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {subTab === 'expired' && (
        <>
          <div className="section-header">
            <h2 className="section-title">
              <span className="section-title-icon">⏰</span>
              Expired Payment Links
            </h2>
            <p style={{ color: '#666', fontSize: '0.8rem', margin: 0 }}>
              QR was shown to the customer, but they never completed payment. <strong style={{ color: '#888' }}>No refund needed.</strong>
            </p>
          </div>

          {expiredEntries.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#444' }}>
              No expired payment links — all QR scans resulted in payment ✅
            </div>
          ) : (
            <>
              <div style={{
                background: 'rgba(100,100,100,0.08)', border: '1px solid rgba(255,255,255,0.05)',
                borderRadius: '0.75rem', padding: '0.75rem 1.25rem', marginBottom: '1rem',
                display: 'flex', alignItems: 'center', gap: '0.75rem',
              }}>
                <span style={{ fontSize: '1.1rem' }}>ℹ️</span>
                <span style={{ color: '#888', fontSize: '0.82rem' }}>
                  These are payment links where the customer scanned or pressed a button but <strong style={{ color: '#aaa' }}>never completed the UPI payment</strong>.
                  No money was charged — no action needed.
                </span>
              </div>
              <div className="table-container">
                <table className="data-table">
                  <thead><tr>
                    <th>Order ID</th><th>Payment Link ID</th><th>Amount</th><th>Created At</th><th>Status</th>
                  </tr></thead>
                  <tbody>
                    {expiredEntries.map((entry) => (
                      <tr key={entry.id} style={{ opacity: 0.7 }}>
                        <td className="table-mono" style={{ fontSize: '0.75rem' }}>{entry.id}</td>
                        <td className="table-muted" style={{ fontSize: '0.75rem' }}>{entry.plink_id || '—'}</td>
                        <td style={{ color: '#666' }}>
                          {entry.amount ? `₹${entry.amount}` : '—'}
                        </td>
                        <td className="table-muted">{formatTimestamp(entry.timestamp)}</td>
                        <td>
                          <span className="status-badge" style={{ background: 'rgba(100,100,100,0.15)', color: '#777' }}>
                            ⏰ EXPIRED — No Payment
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-footer">
                <span className="table-count">{expiredEntries.length} expired link{expiredEntries.length !== 1 ? 's' : ''}</span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
